import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/auth';
import {
  tripBriefsService,
  SENSITIVITY_METRIC_KEYS,
  SENSITIVITY_WEIGHT_STEPS,
  type SensitivityMetricKey,
  type SensitivityWeights,
} from '@/services/trip-briefs.service';

/** Roles allowed to change the org-wide scoring weights (mirrors the service gate). */
const SETTINGS_ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN', 'SECURITY_MANAGER'];

const METRIC_LABELS: Record<SensitivityMetricKey, string> = {
  country: 'Country / overall',
  crime: 'Crime',
  terrorism: 'Terrorism',
  political: 'Political stability',
  conflict: 'Armed conflict',
  kidnapping: 'Kidnapping',
  health: 'Health / medical',
  traffic: 'Road / traffic safety',
  environmental: 'Weather / environmental',
  cyber: 'Cybersecurity',
  visa: 'Visa / entry',
};

const WEIGHT_LABELS: Record<number, string> = {
  0: 'Off',
  0.5: 'Low',
  1: 'Standard',
  1.5: 'High',
  2: 'Critical',
};

const DEFAULT_WEIGHTS: SensitivityWeights = SENSITIVITY_METRIC_KEYS.reduce(
  (acc, k) => ({ ...acc, [k]: 1 }),
  {} as SensitivityWeights,
);

export default function TravelRiskSettings() {
  const queryClient = useQueryClient();
  const checkAnyRole = useAuthStore((state) => state.checkAnyRole);
  const canEdit = checkAnyRole(SETTINGS_ADMIN_ROLES);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['travel-risk-settings'],
    queryFn: () => tripBriefsService.getSensitivitySettings(),
  });

  const [weights, setWeights] = useState<SensitivityWeights>(DEFAULT_WEIGHTS);

  useEffect(() => {
    if (data?.metricWeights) {
      setWeights({ ...DEFAULT_WEIGHTS, ...data.metricWeights });
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (next: SensitivityWeights) => tripBriefsService.updateSensitivitySettings(next),
    onSuccess: (saved) => {
      queryClient.setQueryData(['travel-risk-settings'], saved);
      toast.success('Sensitivity settings saved. New briefs will use these weights.');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to save sensitivity settings');
    },
  });

  const dirty = useMemo(
    () => SENSITIVITY_METRIC_KEYS.some((k) => weights[k] !== (data?.metricWeights?.[k] ?? 1)),
    [weights, data],
  );

  const setWeight = (key: SensitivityMetricKey, value: number) =>
    setWeights((prev) => ({ ...prev, [key]: value }));

  if (isLoading) {
    return <div className="text-secondary-600 dark:text-secondary-400">Loading sensitivity settings…</div>;
  }
  if (isError) {
    return <div className="text-danger-600 dark:text-danger-400">Failed to load sensitivity settings.</div>;
  }

  return (
    <div className="space-y-6" data-testid="travel-risk-settings">
      <Card>
        <CardContent className="p-6 space-y-2">
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
            Travel Risk Sensitivity
          </h2>
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            Set how strongly each risk dimension influences a brief&apos;s weighted Go/No-Go score.
            The final score is a weighted average of all eleven dimensions; higher weights make a
            dimension count for more, <strong>Off</strong> excludes it entirely.
          </p>
          <p className="text-sm text-warning-700 dark:text-warning-400">
            Note: because the score is an average, a single elevated dimension no longer forces a
            NO-GO on its own. To make one dimension dominate (e.g. armed conflict), raise its weight
            to <strong>Critical</strong> and lower the others.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          {SENSITIVITY_METRIC_KEYS.map((key) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <label htmlFor={`weight-${key}`} className="text-sm font-medium text-secondary-900 dark:text-white">
                {METRIC_LABELS[key]}
              </label>
              <select
                id={`weight-${key}`}
                data-testid={`weight-${key}`}
                disabled={!canEdit}
                value={weights[key]}
                onChange={(e) => setWeight(key, Number(e.target.value))}
                className="w-40 rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm text-secondary-900 disabled:opacity-60 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white"
              >
                {SENSITIVITY_WEIGHT_STEPS.map((step) => (
                  <option key={step} value={step}>
                    {WEIGHT_LABELS[step]} ({step}×)
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div className="flex items-center justify-between pt-2 border-t border-secondary-200 dark:border-secondary-800">
            <span className="text-xs text-secondary-500 dark:text-secondary-400">
              {data?.updatedByName ? `Last updated by ${data.updatedByName}` : 'Using default weights'}
            </span>
            <div className="flex gap-3">
              <Button
                variant="outline"
                disabled={!canEdit || !dirty || mutation.isPending}
                onClick={() => setWeights({ ...DEFAULT_WEIGHTS, ...(data?.metricWeights ?? {}) })}
              >
                Reset
              </Button>
              <Button
                disabled={!canEdit || !dirty || mutation.isPending}
                onClick={() => mutation.mutate(weights)}
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
          {!canEdit && (
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              You have read-only access. Only security managers and admins can change these weights.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
